<template>
  <div class="task-form">
    <CForm @submit="onFormSubmit" id="taskForm">
      <CInput
          v-model="form.name"
          label="Ime opravila"
          required
      />

      <CTextarea
          v-model="form.description"
          label="Opis"
          rows="3"
      />

      <div v-if="showSchedulingMode" class="form-group">
        <label class="col-form-label">Razporeditev</label>
        <div>
          <div class="form-check form-check-inline">
            <input
                class="form-check-input"
                type="radio"
                id="mode-auto"
                value="auto"
                v-model="form.schedulingMode"
                :disabled="isAutoschedulingDisabled"
            />
            <label class="form-check-label" for="mode-auto">Samodejno</label>
          </div>
          <div class="form-check form-check-inline">
            <input
                class="form-check-input"
                type="radio"
                id="mode-manual"
                value="manual"
                v-model="form.schedulingMode"
            />
            <label class="form-check-label" for="mode-manual">Ročno</label>
          </div>
          <div class="form-check form-check-inline">
            <input
                class="form-check-input"
                type="radio"
                id="mode-pending"
                value="pending"
                v-model="form.schedulingMode"
            />
            <label class="form-check-label" for="mode-pending">Čakajoče</label>
          </div>
        </div>
      </div>

      <CInput
          v-if="showTimeDuration"
          v-model.number="form.time"
          label="Potreben čas"
          type="number"
          :step="timeStepValue"
          min="0"
          max="24"
      />

      <div v-if="showDatetimePickers">
        <div class="form-row" style="display: flex; gap: 15px;">
          <div class="form-group" style="flex: 1;">
            <label class="col-form-label">Začetni čas *</label>
            <input
                v-model="form.started_at"
                type="datetime-local"
                class="form-control"
                :step="timeStepSeconds"
            />
          </div>

          <div class="form-group" style="flex: 1;">
            <label class="col-form-label">Končni čas *</label>
            <input
                v-model="form.completed_at"
                type="datetime-local"
                class="form-control"
                :step="timeStepSeconds"
            />
          </div>
        </div>

        <div style="color: #d97706; font-style: italic; margin-bottom: 15px;">
          Opomba: Čas mora biti v formatu z {{ schedulerCellDuration }} minutnimi intervali (npr. {{ timeIntervalExamples }}).
        </div>
      </div>

      <div v-if="showUrgentCheckbox" class="form-group">
        <CInputCheckbox
            :label="'Nujno opravilo'"
            :checked="form.is_urgent"
            @change="e => form.is_urgent = e.target.checked"
            color="primary"
        />
      </div>

      <div v-if="showTimeLockCheckbox" class="form-group">
        <CInputCheckbox
            :label="'Zakleni čas'"
            :checked="form.is_time_locked"
            @change="e => form.is_time_locked = e.target.checked"
            color="primary"
        />
      </div>

      <div v-if="showWorkDetails && task && task.workProcess" class="form-group">
        <label class="col-form-label"><strong>Podrobnosti naloge</strong></label>
        <div style="padding: 10px; background-color: #f8f9fa; border-radius: 5px;">
          <div><strong>Delovni proces:</strong> {{ task.workProcess.name }}</div>
          <template v-if="task.sp_orders_data && task.sp_orders_data.length > 0">
            <div v-for="(order, index) in task.sp_orders_data" :key="index">
              <div><strong>Naročilo:</strong> <a :href="`/#/narocanjend/list?edit_order=${order.id}`" target="_blank">{{ order.number }}</a></div>
              <div><strong>Stranka:</strong> {{ order.client_name }}</div>
              <div><strong>Vozilo:</strong> {{ order.vehicle_data }}</div>
            </div>
          </template>
        </div>
      </div>

      <div v-if="showWorkerFree" class="form-group">
        <CInputCheckbox
            :label="'Ali je delavec med opravilo prost'"
            :checked="form.is_worker_free"
            @change="handleWorkerFreeChange"
            color="primary"
        />
      </div>
      <br v-if="showWorkerFree">

      <CSelect
          v-if="showRoleSelect"
          :value="form.role"
          label="Rola"
          :options="roleOptions"
          @change="handleRoleChange"
          required
      />

      <v-select
          v-if="showUserSelect"
          v-model="form.default_user_id"
          :options="users"
          label="name"
          :reduce="user => user.id"
          @search="searchUsers"
          placeholder="Prednastavljen delavec"
          :required="requireUser"
      >
        <template #no-options-message>
          Začni pisati za iskanje
        </template>
      </v-select>
    </CForm>
    <vue-toastr ref="toastr"></vue-toastr>
  </div>
</template>

<script>
import axios from 'axios'
import settings from '@/settings'

export default {
  name: 'TaskForm',

  props: {
    task: {
      type: Object,
      default: () => ({})
    },
    workProcessId: {
      type: [Number, String]
    },
    orderId: {
      type: [String, Number],
      default: null,
    },
    showTaskModal: Boolean,
    formType: {
      type: String,
      default: 'normal',
      validator: value => ['normal', 'extended'].includes(value)
    }
  },

  data() {
    return {
      form: {
        name: '',
        description: '',
        time: null,
        role: '',
        default_user_id: null,
        is_worker_free: false,
        work_processes_id: this.workProcessId,
        started_at: '',
        completed_at: '',
        is_urgent: false,
        is_time_locked: false,
        schedulingMode: 'manual',
      },
      users: [],
      roleOptions: [
        {value: '', label: 'Izberi'},
        {value: 'licar', label: 'Licacar'},
        {value: 'klepar', label: 'Klepar'}
      ]
    }
  },

  computed: {
    isExtended() {
      return this.formType === 'extended'
    },
    isEditMode() {
      return !!(this.task && this.task.id)
    },
    isWorkerFree() {
      return this.form.is_worker_free
    },
    isManualScheduling() {
      return this.isExtended && !this.isWorkerFree && this.form.schedulingMode === 'manual'
    },
    isSpOrder() {
      return !!this.orderId
    },

    // Field visibility
    showSchedulingMode() {
      return this.isExtended && !this.isWorkerFree
    },
    showTimeDuration() {
      return !this.isExtended
        || this.isWorkerFree
        || this.form.schedulingMode === 'auto'
        || this.form.schedulingMode === 'pending'
    },
    showDatetimePickers() {
      return this.isManualScheduling
    },
    showUrgentCheckbox() {
      return this.isManualScheduling
    },
    showTimeLockCheckbox() {
      return this.isManualScheduling
    },
    showWorkerFree() {
      return !this.isExtended || this.isSpOrder
    },
    showRoleSelect() {
      return !this.isWorkerFree
    },
    showUserSelect() {
      return !this.isWorkerFree && !!this.form.role
    },
    showWorkDetails() {
      return this.isExtended && this.isEditMode
    },
    isAutoschedulingDisabled() {
      return this.isEditMode && !!(this.task?.started_at || this.task?.completed_at)
    },
    requireUser() {
      return this.isManualScheduling
    },

    // Time step helpers
    timeStepValue() {
      return settings.schedulerCellDurationMinutes / 60
    },
    schedulerCellDuration() {
      return settings.schedulerCellDurationMinutes
    },
    timeStepSeconds() {
      return settings.schedulerCellDurationMinutes * 60
    },
    timeIntervalExamples() {
      const intervals = []
      for (let i = 0; i < 60; i += settings.schedulerCellDurationMinutes) {
        intervals.push(`${String(i).padStart(2, '0')}`)
      }
      return intervals.map(m => `XX:${m}`).join(', ')
    }
  },

  watch: {
    task: {
      handler(task) {
        this.form = {
          ...this.form,
          ...task
        }

        if (task?.default_user) {
          this.users = [task.default_user]
        }

        if (task?.role) {
          this.form.role = typeof task.role === 'string' ? task.role : task.role.name
        }

        // Derive is_time_locked from earliest_start_at
        this.form.is_time_locked = !!task?.earliest_start_at

        // Always open existing tasks in manual scheduling mode
        if (task?.id) {
          this.form.schedulingMode = 'manual'
        }

        // Pre-format datetime fields for the datetime-local inputs
        if (this.isExtended) {
          if (task?.started_at) {
            this.form.started_at = this.formatDatetimeForInput(task.started_at)
          }
          if (task?.completed_at) {
            this.form.completed_at = this.formatDatetimeForInput(task.completed_at)
          }
        }
      },
      immediate: true
    },

    showTaskModal: {
      handler(newVal) {
        if (!newVal) {
          this.form = {
            name: '',
            description: '',
            time: null,
            role: '',
            default_user_id: null,
            is_worker_free: false,
            work_processes_id: this.workProcessId,
            started_at: '',
            completed_at: '',
            is_urgent: false,
            is_time_locked: false,
            schedulingMode: 'manual',
          }
        }
      },
      immediate: true
    }
  },

  methods: {
    onFormSubmit(e) {
      e.preventDefault()
      this.handleSubmit()
    },

    handleWorkerFreeChange(event) {
      this.form.is_worker_free = event.target.checked
      if (this.form.is_worker_free) {
        this.form.role = ''
        this.form.default_user_id = null
        this.users = []
      }
    },

    handleRoleChange(event) {
      this.form.role = typeof event === 'object' ? event.target?.value : event
      this.form.default_user_id = null
      this.users = []
    },

    searchUsers(search) {
      if (!this.form.role) return Promise.resolve()

      return axios.get(`/users/search`, {
        params: { search, role: this.form.role }
      })
        .then(response => {
          this.users = response.data.users
        })
        .catch(() => {
          this.$refs.toastr.e('Error', 'Failed to fetch users')
        })
    },

    formatDatetimeForInput(datetime) {
      // Convert ISO datetime to format compatible with datetime-local input (YYYY-MM-DDTHH:mm)
      const date = new Date(datetime)
      const pad = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },

    formatDatetimeForApi(datetime) {
      // Convert datetime-local input value to API format (yyyy-MM-dd HH:mm:ss)
      if (!datetime) return null
      const date = new Date(datetime)
      const pad = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`
    },

    validateTimeInterval(datetime) {
      const date = new Date(datetime)
      return date.getMinutes() % settings.schedulerCellDurationMinutes === 0
    },

    handleSubmit() {
      // --- Validation ---

      if (this.isExtended && this.showTimeDuration && (!this.form.time || this.form.time <= 0)) {
        this.$refs.toastr.e('Napaka', 'Vnesite veljaven potreben čas')
        return
      }

      if (this.isManualScheduling) {
        if (this.requireUser && !this.form.default_user_id) {
          this.$refs.toastr.e('Napaka', 'Izbrati morate uporabnika')
          return
        }

        if (!this.form.started_at || !this.form.completed_at) {
          this.$refs.toastr.e('Napaka', 'Začetni in končni čas sta obvezna')
          return
        }

        const start = new Date(this.form.started_at)
        const end = new Date(this.form.completed_at)

        if (start >= end) {
          this.$refs.toastr.e('Napaka', 'Začetni čas mora biti pred končnim časom')
          return
        }

        if (!this.validateTimeInterval(this.form.started_at)) {
          this.$refs.toastr.e('Napaka', `Začetni čas mora biti v ${settings.schedulerCellDurationMinutes} minutnih intervalih`)
          return
        }

        if (!this.validateTimeInterval(this.form.completed_at)) {
          this.$refs.toastr.e('Napaka', `Končni čas mora biti v ${settings.schedulerCellDurationMinutes} minutnih intervalih`)
          return
        }

        // Calculate duration from the selected time range
        const diffHours = Math.max(0.25, (end - start) / (1000 * 60 * 60))
        this.form.time = +diffHours.toFixed(2)
      }

      // --- Build payload ---

      if (!this.isExtended) {
        // Normal form: template task, no scheduling
        const payload = {
          name: this.form.name,
          description: this.form.description,
          time: this.form.time,
          role: this.form.role,
          default_user_id: this.form.default_user_id,
          is_worker_free: this.form.is_worker_free,
          work_processes_id: this.form.work_processes_id,
        }
        if (this.form.id) payload.id = this.form.id
        this.$emit('submit', payload)
        return
      }

      // Extended form
      const payload = {
        name: this.form.name,
        description: this.form.description,
        time: this.form.time,
        role: this.form.role,
        default_user_id: this.form.default_user_id || null,
        work_processes_id: this.form.work_processes_id,
      }

      if (this.form.id) payload.id = this.form.id

      if (this.isWorkerFree) {
        payload.is_worker_free = true
      } else {
        payload.scheduling_mode = this.form.schedulingMode
        if (this.form.schedulingMode === 'manual') {
          payload.started_at = this.formatDatetimeForApi(this.form.started_at)
          payload.completed_at = this.formatDatetimeForApi(this.form.completed_at)
          payload.is_urgent = this.form.is_urgent || false
          payload.is_time_locked = this.form.is_time_locked || false
        }
      }

      this.$emit('submit', payload)
    }
  }
}
</script>